import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  CustomSelectOption,
  CustomSelectProps,
} from "../../types/ComponentProps";
import CustomSelectStyles from "./CustomSelectStyles.module.css";

const CustomSelect = ({
  options,
  value,
  onSelect,
  placeholder,
}: CustomSelectProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] =
    useState<CustomSelectOption | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const listId = `custom-select-list-${Math.random().toString(36).substr(2, 9)}`;

  // Sync selected option with value
  useEffect(() => {
    const found = options.find((opt) => opt.value === value);
    setSelectedOption(found || null);
  }, [value, options]);

  const handleSelectOption = useCallback((option: CustomSelectOption) => {
    setSelectedOption(option);
    onSelect(option);
    setIsOpen(false);
    setSearchTerm("");
    setActiveIndex(-1);
    inputRef.current?.focus();
  }, [onSelect]);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setActiveIndex(-1);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  const [searchTerm, setSearchTerm] = useState<string>("");

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setIsOpen(true);
    setActiveIndex(-1);
  };

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex(0);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) => {
          const next = prev < filteredOptions.length - 1 ? prev + 1 : 0;
          return next;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) => {
          const next = prev > 0 ? prev - 1 : filteredOptions.length - 1;
          return next;
        });
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[activeIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setActiveIndex(-1);
        break;
      case "Tab":
        setIsOpen(false);
        setActiveIndex(-1);
        break;
      case "Home":
        e.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        e.preventDefault();
        setActiveIndex(filteredOptions.length - 1);
        break;
    }
  }, [isOpen, filteredOptions, activeIndex, handleSelectOption]);

  // Scroll active item into view
  useEffect(() => {
    if (isOpen && activeIndex >= 0 && listRef.current) {
      const activeItem = listRef.current.children[activeIndex] as HTMLElement;
      if (activeItem) {
        activeItem.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex, isOpen]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const activeOptionId = activeIndex >= 0 ? `option-${activeIndex}` : undefined;

  return (
    <div className={CustomSelectStyles.customSelect} ref={dropdownRef}>
      <div className={CustomSelectStyles.selectDisplay}>
        <input
          ref={inputRef}
          type="text"
          value={searchTerm || selectedOption?.label || ""}
          onChange={handleSearch}
          onClick={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listId}
          aria-activedescendant={activeOptionId}
          aria-autocomplete="list"
        />
        <button
          type="button"
          className={CustomSelectStyles.dropdownToggle}
          onClick={toggleDropdown}
          tabIndex={-1}
          aria-label={isOpen ? "Close dropdown" : "Open dropdown"}
        >
          <i
            className={`fa-solid ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`}
            aria-hidden="true"
          />
        </button>
      </div>
      {isOpen && (
        <ul 
          ref={listRef}
          id={listId}
          className={CustomSelectStyles.optionList}
          role="listbox"
          aria-label="Options"
        >
          {filteredOptions.map((option, index) => (
            <li
              key={option.value}
              id={`option-${index}`}
              className={`${CustomSelectStyles.optionItem} ${
                index === activeIndex ? CustomSelectStyles.optionItemActive : ""
              } ${selectedOption?.value === option.value ? CustomSelectStyles.optionItemSelected : ""}`}
              onClick={() => {
                handleSelectOption(option);
              }}
              onMouseEnter={() => setActiveIndex(index)}
              role="option"
              aria-selected={selectedOption?.value === option.value}
            >
              {option.label}
            </li>
          ))}
          {filteredOptions.length === 0 && (
            <li className={CustomSelectStyles.noResults} role="option" aria-selected={false} aria-disabled="true">
              No results found
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default CustomSelect;
